import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import SearchIcon from "@tCentro/Icons/SearchIcon";
import React, { FC, useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
import translate from "@insite/client-framework/Translate";
import CustomApplicationState from "@tCentro/Store/CustomApplicationState";
import Img, { ImgProps } from "@insite/mobius/Img";
import { COLORS, TYPOGRAPHY } from "@tCentro/theme/design";
import TextField, { TextFieldPresentationProps } from "@insite/mobius/TextField";
import { BrandCategoryModel } from "@insite/client-framework/Types/ApiModels";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import breakpointMediaQueries from "@insite/mobius/utilities/breakpointMediaQueries";

const mapStateToProps = (state: ApplicationState & CustomApplicationState) => {
    const brandCategoriesData = state.t_components.CustomBrandData.brandCategoriesData;
    const isLoading = state.t_components.CustomBrandData.isloading;
    return {
        brandCategoriesData,
        isLoading,
    };
};

const mapDispatchToProps = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface NISProductCategoriesViewStyles {
    wrapper?: InjectableCss;
    categoryWrapper?: InjectableCss;
    categoryLink?: LinkPresentationProps;
    img?: ImgProps;
    imgLink?: LinkPresentationProps;
    searchTextField?: TextFieldPresentationProps;
    categoryMainWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
}

export const productCategoriesViewStyles: NISProductCategoriesViewStyles = {
    wrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            gap: 20px;
            margin-bottom: 0px;
            @media (max-width: 767px) {
                margin-bottom: 20px;
            }

            @media (max-width: 991px) {
                gap: 8px;
            }
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding-right: 16px;
                        padding-left: 16px;
                    `,
                    css`
                        padding-right: 16px;
                        padding-left: 16px;
                    `,
                    css`
                        padding-right: 16px;
                    `,
                    css`
                        padding-right: 64px;
                    `,
                    css`
                        padding-right: 64px;
                    `,
                ])}
        `,
    },
    categoryWrapper: {
        css: css`
            margin-bottom: 0px;
            display: flex;
            flex-direction: column;
            align-items: center;
            text-align: center;
            border: 1px solid #e0dfe0;
            border-radius: 8px;
            padding: 16px;
            @media (max-width: 767px) {
                margin-bottom: 0px;
            }
        `,
    },
    img: {
        height: 160,
        css: css`
            cursor: pointer;
            width: 100%;
            object-fit: contain;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        height: 90px;
                        object-fit: contain;
                    `,
                    css`
                        height: 90px;
                    `,
                    css`
                        height: 115px;
                    `,
                    css`
                        height: 160px;
                    `,
                    null,
                ])}
        `,
    },
    categoryLink: {
        css: css`
            margin-top: 10px;
            font-family: Roboto Condensed;
            font-weight: 700;
            font-size: 16px;
            line-height: 24px;
            color: #333333;
            text-transform: uppercase;
            text-decoration: none;
            & > span {
                color: #333333 !important;
            }
            cursor: pointer;
            &:focus,
            &:hover {
                text-decoration: none;
                outline: none;
            }
        `,
    },
    imgLink: {
        css: css`
            text-decoration: none;
            &:focus,
            &:hover {
                text-decoration: none;
                outline: none;
            }
        `,
    },
    searchTextField: {
        iconProps: { src: SearchIcon },
        cssOverrides: {
            formField: css`
                width: 100%;

                margin-top: 25px;
                div {
                    input {
                        border-radius: 16px !important;
                        padding: 0px 24px !important;
                        height: 48px;
                        background: ${COLORS.background.base} !important;
                        color: ${COLORS.black} !important;
                        font-size: ${TYPOGRAPHY.size.smaller} !important;
                        font-weight: ${TYPOGRAPHY.weight.base};
                        border: 1px solid #e0dfe0 !important;
                    }
                    button {
                        outline: none;
                    }
                    span {
                        top: 15px !important;
                        right: 26px !important;
                        width: 18px !important;
                        height: 18px !important;
                        padding: 0px !important;
                        svg {
                            width: 18px;
                            height: 18px;
                        }
                    }
                }
            `,
        },
    },
    categoryMainWrapper: {
        css: css`
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 26px;
            margin-top: 10px;
            margin-bottom: 0px;
            width: 100%;
            @media (max-width: 1199px) {
                grid-template-columns: repeat(2, 1fr);
            }
            @media (max-width: 767px) {
                grid-template-columns: repeat(2, 1fr);
                margin-top: 0px;
                gap: 8px;
            }
        `,
    },
    spinner: {
        css: css`
            margin: auto;
        `,
    },
};

const styles = productCategoriesViewStyles;

const NISProductCategoriesView: FC<Props> = ({ brandCategoriesData, isLoading }) => {
    const [searchText, setSearchText] = useState("");
    const [searchCategoryData, setSearchCategoryData] = useState<BrandCategoryModel[]>([]);

    useEffect(() => {
        setSearchCategoryData(brandCategoriesData);
        setSearchText("");
    }, [brandCategoriesData]);

    const onChangeSearchText = (event: React.ChangeEvent<HTMLInputElement>) => {
        const filteredText = event.currentTarget.value;
        const filteredCategoryData = brandCategoriesData.filter(category =>
            category.categoryName.toLowerCase().includes(filteredText.toLowerCase()),
        );
        setSearchCategoryData(filteredCategoryData);
        setSearchText(event.currentTarget.value);
    };

    const doSearch = () => {
        setSearchText("");
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === "Enter") {
            event.preventDefault();
            doSearch();
        } else {
            setSearchText(event.currentTarget.value);
        }
    };

    return (
        <StyledWrapper {...styles.wrapper} className="categoriesWrappper">
            <TextField
                {...styles.searchTextField}
                iconClickableProps={{ onClick: doSearch, "aria-label": `Search within categories` }}
                value={searchText}
                onChange={event => onChangeSearchText(event)}
                onKeyDown={handleKeyDown}
                placeholder={translate("Search product categories...")}
                type="text"
                data-test-selector="NISProductCategoriesSearchWithin"
                id="productsKeywordSearch"
            />

            {isLoading ? (
                <LoadingSpinner {...styles.spinner} />
            ) : (
                <StyledWrapper {...styles.categoryMainWrapper}>
                    {(searchCategoryData &&
                        searchCategoryData.length > 0 &&
                        searchCategoryData.map((category, index) => {
                            return (
                                <StyledWrapper
                                    key={`categories_${category.categoryId}_${index}`}
                                    {...styles.categoryWrapper}
                                >
                                    <Link href={category.productListPagePath} {...styles.imgLink}>
                                        <Img
                                            src={category.featuredImagePath}
                                            altText={category.categoryName}
                                            title={category.categoryName}
                                            {...styles.img}
                                        />
                                    </Link>
                                    <Link href={category.productListPagePath} {...styles.categoryLink}>
                                        {category.categoryName}
                                    </Link>
                                </StyledWrapper>
                            );
                        })) || <Typography variant="h6">{translate("No Categories Found")}</Typography>}
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(NISProductCategoriesView),
    definition: {
        displayName: "NIS Product Categories View",
        group: "Basic",
        icon: "Category",
        fieldDefinitions: [],
    },
};

export default widgetModule;
